import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { formatItemDisplayName } from './formatItemDisplayName';

/**
 * Format date for display (dd/MM/yyyy).
 */
function formatDate(value, dateFormat = 'dd/MM/yyyy') {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  switch (dateFormat) {
    case 'MM/dd/yyyy':
      return `${mm}/${dd}/${yyyy}`;
    case 'yyyy-MM-dd':
      return `${yyyy}-${mm}-${dd}`;
    case 'dd-MM-yyyy':
      return `${dd}-${mm}-${yyyy}`;
    case 'dd/MM/yyyy':
    default:
      return `${dd}/${mm}/${yyyy}`;
  }
}

/**
 * Fetch image from URL and return as data URL (for embedding in PDF).
 * @param {string} url - Full image URL
 * @returns {Promise<string|null>} - data URL or null on failure
 */
async function fetchLogoAsDataUrl(url) {
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) return null;
    const blob = await res.blob();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

/**
 * Export a sales order (invoice) to PDF and optionally save or return blob.
 * @param {Object} order - Sales order with items, customer, totals
 * @param {Object} company - Company with name, address, currency, dateFormat, printItemNameFormat, logoUrl
 * @param {Object} [options]
 * @param {string} [options.filename] - Download filename without extension (default: invoice number)
 * @param {string} [options.itemFormat] - name_sku, name, etc. (default: company.printItemNameFormat)
 * @param {boolean} [options.save=true] - If true, triggers browser download
 * @param {string} [options.logoDataUrl] - Optional pre-loaded logo as data URL (if not set, logoUrl is fetched when present)
 * @returns {Promise<Uint8Array|undefined>} - PDF bytes if save is false
 */
export async function exportSalesOrderToPdf(order, company, options = {}) {
  let logoDataUrl = options.logoDataUrl;
  if (!logoDataUrl && company?.logoUrl) {
    logoDataUrl = await fetchLogoAsDataUrl(company.logoUrl);
  }

  const {
    filename = (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-'),
    itemFormat = company?.printItemNameFormat || 'name_sku',
    save = true,
  } = options;

  const currency = company?.currency || 'USD';
  const dateFormat = company?.dateFormat || 'dd/MM/yyyy';

  const formatAmount = (value) => {
    const amount = Number(value ?? 0);
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency , minimumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2), maximumFractionDigits: (typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)}).format(amount);
    } catch {
      return `${currency} ${amount.toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2))}`;
    }
  };

  const formatItemName = (item) =>
    formatItemDisplayName(
      {
        name: item.product?.name,
        sku: item.product?.sku,
        brand: item.product?.brand,
      },
      itemFormat
    );

  const pdfFormat = company?.printPaperType === 'a5' ? 'a5' : 'a4';
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: pdfFormat });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 14;
  const logoSize = 22;
  const logoRight = margin + logoSize + 4;
  let y = 14;

  // Company logo (if available)
  if (logoDataUrl) {
    try {
      const format = logoDataUrl.startsWith('data:image/png') ? 'PNG' : 'JPEG';
      doc.addImage(logoDataUrl, format, margin, y, logoSize, logoSize);
    } catch (e) {
      // ignore invalid image
    }
  }

  // Company name (to the right of logo if logo present)
  const companyX = logoDataUrl ? logoRight : margin;
  doc.setFontSize(16);
  doc.setFont(undefined, 'bold');
  doc.text(company?.name || 'Company', companyX, y + (logoDataUrl ? 5 : 0));
  y += 7;

  if (company?.address) {
    doc.setFontSize(9);
    doc.setFont(undefined, 'normal');
    const addrWidth = pageWidth - companyX - margin;
    const addrLines = doc.splitTextToSize(company.address, addrWidth);
    addrLines.forEach((line) => {
      doc.text(line, companyX, y);
      y += 4;
    });
    y += 2;
  }

  // Invoice title and meta (right-aligned)
  doc.setFontSize(14);
  doc.setFont(undefined, 'bold');
  doc.text('Invoice', pageWidth - margin, 14, { align: 'right' });
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text(`Invoice No: ${order.invoiceNumber || order.orderNumber || '-'}`, pageWidth - margin, 20, { align: 'right' });
  doc.text(`Date: ${formatDate(order.invoiceDate, dateFormat)}`, pageWidth - margin, 25, { align: 'right' });
  doc.text(`Sales Type: ${order.salesType || 'credit'}`, pageWidth - margin, 30, { align: 'right' });

  // Ensure content below logo/header does not overlap
  if (logoDataUrl) {
    y = Math.max(y, 14 + logoSize + 4);
  } else {
    y = Math.max(y, 36);
  }
  y += 4;

  // Bill To
  doc.setFontSize(10);
  doc.setFont(undefined, 'bold');
  doc.text('Bill To', margin, y);
  y += 5;
  doc.setFont(undefined, 'normal');
  doc.setFontSize(9);
  doc.text(order.customer?.name || '-', margin, y);
  y += 5;
  if (order.customer?.address) {
    const custAddr = doc.splitTextToSize(order.customer.address, pageWidth * 0.4);
    custAddr.forEach((line) => {
      doc.text(line, margin, y);
      y += 4;
    });
  }
  if (order.customer?.gstin || order.customer?.trn) {
    doc.text(`VAT Number: ${order.customer.gstin || order.customer.trn}`, margin, y);
    y += 5;
  }
  y += 6;

  // Items table
  const items = order.items || [];
  const tableHeaders = ['#', 'Item', 'Qty', 'Unit', 'Price', 'Discount', 'VAT', 'Line Total'];
  const tableRows = items.map((it, idx) => [
    String(idx + 1),
    formatItemName(it),
    Number(it.quantity ?? 0).toFixed((typeof company !== 'undefined' ? (company?.currencyDecimals ?? 2) : 2)),
    it.unit || 'pcs',
    formatAmount(it.unitPrice),
    formatAmount(it.discount ?? 0),
    formatAmount(it.vatAmount ?? 0),
    formatAmount(it.total ?? 0),
  ]);

  autoTable(doc, {
    startY: y,
    head: [tableHeaders],
    body: tableRows,
    margin: { left: margin, right: margin },
    styles: { fontSize: 8 },
    headStyles: { fillColor: [66, 66, 66], textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    tableLineColor: [200, 200, 200],
    tableLineWidth: 0.1,
  });

  y = doc.lastAutoTable.finalY + 10;

  // Totals block (right-aligned)
  const totalsLeft = pageWidth - margin - 60;
  doc.setFontSize(9);
  doc.setFont(undefined, 'normal');
  doc.text('Subtotal', totalsLeft, y);
  doc.text(formatAmount(order.subtotal), pageWidth - margin, y, { align: 'right' });
  y += 5;
  doc.text('Discount', totalsLeft, y);
  doc.text(formatAmount(order.discountTotal), pageWidth - margin, y, { align: 'right' });
  y += 5;
  doc.text('VAT', totalsLeft, y);
  doc.text(formatAmount(order.vatTotal), pageWidth - margin, y, { align: 'right' });
  
  if (Number(order.roundOff)) {
    y += 5;
    doc.text('Round Off', totalsLeft, y);
    doc.text(formatAmount(order.roundOff), pageWidth - margin, y, { align: 'right' });
  }

  y += 6;
  doc.setFont(undefined, 'bold');
  doc.text('Grand Total', totalsLeft, y);
  doc.text(formatAmount(Number(order.grandTotal ?? 0) + Number(order.roundOff ?? 0)), pageWidth - margin, y, { align: 'right' });
  y += 5;
  doc.setFont(undefined, 'normal');
  doc.text('Paid', totalsLeft, y);
  doc.text(formatAmount(order.paidAmount), pageWidth - margin, y, { align: 'right' });
  y += 5;
  doc.text('Balance', totalsLeft, y);
  doc.text(formatAmount(order.balanceAmount), pageWidth - margin, y, { align: 'right' });
  y += 8;

  if (order.notes) {
    doc.setFont(undefined, 'bold');
    doc.text('Notes', margin, y);
    y += 5;
    doc.setFont(undefined, 'normal');
    const noteLines = doc.splitTextToSize(order.notes, pageWidth - margin * 2);
    noteLines.forEach((line) => {
      doc.text(line, margin, y);
      y += 4;
    });
  }

  const safeName = `${filename}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');
  if (save) {
    doc.save(safeName);
    return undefined;
  }
  return doc.output('arraybuffer');
}


/**
 * Share sales order as PDF: opens native share (e.g. email, messaging).
 * Falls back to download if Web Share API is not available or user cancels.
 * @param {Object} order - Sales order
 * @param {Object} company - Company
 * @param {Object} [options] - Same as exportSalesOrderToPdf (filename, itemFormat)
 */
export async function shareSalesOrderPdf(order, company, options = {}) {
  const filename = (options.filename ?? (order.invoiceNumber || order.orderNumber || 'invoice').replace(/\s+/g, '-')).replace(
    /\s+/g,
    '-'
  );
  const safeName = `${filename}.pdf`.replace(/[^a-zA-Z0-9._-]/g, '_');

  const arraybuffer = await exportSalesOrderToPdf(order, company, { ...options, save: false });
  if (!arraybuffer) return;
  const blob = new Blob([arraybuffer], { type: 'application/pdf' });
  
  let file = null;
  try {
    file = new File([blob], safeName, { type: 'application/pdf' });
  } catch (e) {
    // Ignore File constructor error on some Android browsers
  }

  const canShareFiles =
    typeof navigator !== 'undefined' &&
    !!navigator.share &&
    !!navigator.canShare &&
    !!file &&
    navigator.canShare({ files: [file] });

  if (canShareFiles) {
    try {
      await navigator.share({
        title: `Invoice ${order.invoiceNumber || order.orderNumber || ''}`,
        text: company?.name ? `Invoice from ${company.name}` : 'Invoice',
        files: [file],
      });
      return;
    } catch (err) {
      if (err?.name === 'AbortError') return;
      // Fall through to download
    }
  }

  try {
    const isWebView = typeof navigator !== 'undefined' && navigator.userAgent.match(/FBAN|FBAV|Instagram|LinkedInApp|WhatsApp/i);
    if (!isWebView) {
      await exportSalesOrderToPdf(order, company, { ...options, filename, save: true });
    } else {
      const reader = new FileReader();
      reader.onload = () => {
        window.location.href = reader.result;
      };
      reader.readAsDataURL(blob);
    }
  } catch (e) {
    await exportSalesOrderToPdf(order, company, { ...options, filename, save: true });
  }
}
